/* eslint-disable react/jsx-props-no-spreading */
/**
 * There are some shared logics and data across the application
 *
 * This HOC helps to remove the repeated logic and have them only in one place
 */

/**
 * Dependencies
 */
import React from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import {
  app, discovery, quiz, gamification, tracker, header, other
} from '../redux/actions';
import { syncState } from '../helpers/iframeHelper';
import Header from '../common/Header';

/**
 * The HOC to add the shared logic and data among all child components
 *
 * @author: Hesan
 * @param {function} Comp The child component to enhance
 * @returns Enhanced component
 */
const HOC = (Comp) => ({
  projectId,
  districtId,
  appStage,
  uuid,
  discoveryData,
  quizData,
  gamificationData,
  coachables,
  runsOn,
  rrGameOnly,
  accountId,
  viewedEducationData,
  notificationData,
  kidsData,
  headerState,
  profileInfo,
  tookRfQuiz,
  apiGWPrefix,
  dzBrandConfigs,
  tookMmQuiz,
  tookLmQuiz,
  isPreview,
  setProject,
  setDistrict,
  setUuid,
  setLang,
  setRunsOn,
  setRrGameOnly,
  setIsPreview,
  setAppStage,
  setDiscoveryData,
  setQuizState,
  quizState,
  setQuizData,
  updateQuizData,
  updateDiscoveryData,
  setAccountId,
  setApiGWPrefix,
  setDzBrandConfigs,
  setNotificationData,
  setPassedRfQuiz,
  setPassedMmQuiz,
  setTimesTookRfQuiz,
  setTimesTookMmQuiz,
  setTimesTookLmQuiz,
  setHeaderState,
  setBadges,
  location,
  lang,
  learningModulesRedux,
  recycleRight,
  setViewedEducationData,
  mainColor,
  setMainColor
}) => (
  <div>
    <Header
      headerState={headerState}
      setHeaderState={setHeaderState}
    />
    <div style={{ marginTop: 80 }}>
      <Comp
        /**
         * Redux Variables
         */
        projectId={projectId}
        districtId={districtId}
        appStage={appStage}
        lang={lang}
        uuid={uuid}
        discoveryData={discoveryData}
        quizData={quizData}
        coachables={coachables}
        runsOn={runsOn}
        rrGameOnly={rrGameOnly}
        notificationData={notificationData}
        isPreview={isPreview}
        mainColor={mainColor}
        accountId={accountId}
        gamificationData={gamificationData}
        profileInfo={profileInfo}
        tookRfQuiz={tookRfQuiz}
        tookMmQuiz={tookMmQuiz}
        tookLmQuiz={tookLmQuiz}
        kidsData={kidsData}
        quizState={quizState}
        learningModulesRedux={learningModulesRedux}
        recycleRight={recycleRight}
        viewedEducationData={viewedEducationData}
        apiGWPrefix={apiGWPrefix}
        dzBrandConfigs={dzBrandConfigs}
        /**
         * Redux Actions
         */
        setProject={setProject}
        setDistrict={setDistrict}
        setUuid={setUuid}
        setLang={setLang}
        setRunsOn={setRunsOn}
        setRrGameOnly={setRrGameOnly}
        setIsPreview={setIsPreview}
        setMainColor={setMainColor}
        setAccountId={setAccountId}
        setApiGWPrefix={setApiGWPrefix}
        setDzBrandConfigs={setDzBrandConfigs}
        setNotificationData={setNotificationData}
        setAppStage={setAppStage}
        setViewedEducationData={setViewedEducationData}
        setDiscoveryData={setDiscoveryData}
        setQuizData={setQuizData}
        setQuizState={setQuizState}
        updateQuizData={updateQuizData}
        updateDiscoveryData={updateDiscoveryData}
        setPassedRfQuiz={setPassedRfQuiz}
        setPassedMmQuiz={setPassedMmQuiz}
        setBadges={setBadges}
        setTimesTookRfQuiz={setTimesTookRfQuiz}
        setTimesTookMmQuiz={setTimesTookMmQuiz}
        setTimesTookLmQuiz={setTimesTookLmQuiz}
        setHeaderState={setHeaderState}
        /**
         * Functions
         */
        syncState={syncState}
        /**
         * Navigation
         */
        location={location}
      />
    </div>
  </div>
);

/**
 * Action dispatcher for all child components
 *
 * @author: Hesan
 * @param {function} dispatch
 */
function mapDispatchToProps(dispatch) {
  return {
    setProject: (projectId) => dispatch(app.setProjectId(projectId)),
    setDistrict: (districtId) => dispatch(app.setDistrictId(districtId)),
    setUuid: (uuid) => dispatch(app.setUuid(uuid)),
    setLang: (lang) => dispatch(app.setLanguage(lang)),
    setRunsOn: (data) => dispatch(app.setRunsOn(data)),
    setRrGameOnly: (data) => dispatch(app.setRrGameOnly(data)),
    setIsPreview: (isPreview) => dispatch(app.setIsPreview(isPreview)),
    setMainColor: (color) => dispatch(app.setMainColor(color)),
    setApiGWPrefix: (color) => dispatch(app.setApiGWPrefix(color)),
    setDzBrandConfigs: (data) => dispatch(app.setDzBrandConfigs(data)),
    setAccountId: (apiGWPrefix) => dispatch(app.setAccountId(apiGWPrefix)),
    setNotificationData: (notificationId) => dispatch(other.setNotificationData(notificationId)),
    setAppStage: (appStage) => dispatch(app.setAppStage(appStage)),
    setViewedEducationData: (data) => dispatch(other.setViewedEducationData(data)),
    setDiscoveryData: (data) => dispatch(discovery.setDiscoveryData(data)),
    setQuizData: (data) => dispatch(quiz.setQuizData(data)),
    setQuizState: (data) => dispatch(quiz.setQuizState(data)),
    updateQuizData: (data) => dispatch(quiz.updateQuizData(data)),
    updateDiscoveryData: (data) => dispatch(discovery.updateDiscoveryData(data)),
    setPassedRfQuiz: (data) => dispatch(gamification.setPassedRfQuiz(data)),
    setPassedMmQuiz: (data) => dispatch(gamification.setPassedMmQuiz(data)),
    setBadges: (data) => dispatch(gamification.setBadges(data)),
    setTimesTookRfQuiz: (data) => dispatch(tracker.setTimesTookRfQuiz(data)),
    setTimesTookMmQuiz: (data) => dispatch(tracker.setTimesTookMmQuiz(data)),
    setTimesTookLmQuiz: (data) => dispatch(tracker.setTimesTookLmQuiz(data)),
    setHeaderState: (data) => dispatch(header.setHeaderState(data))
  };
}

/**
 * Action state for all child components
 *
 * @author: Hesan
 * @param {object} state
 */
function mapStateToProps(state) {
  return {
    projectId: state.app.projectId,
    districtId: state.app.districtId,
    accountId: state.app.accountId,
    uuid: state.app.uuid,
    viewedEducationData: state.other.viewedEducationData,
    runsOn: state.app.runsOn,
    rrGameOnly: state.app.rrGameOnly,
    notificationData: state.other.notificationData,
    isPreview: state.app.isPreview,
    mainColor: state.app.mainColor,
    apiGWPrefix: state.app.apiGWPrefix,
    dzBrandConfigs: state.app.dzBrandConfigs,
    discoveryData: state.discovery,
    quizData: state.quiz,
    gamificationData: state.gamification,
    profileInfo: state.profileInfo,
    tookRfQuiz: state.tracker.tookRfQuiz,
    tookLmQuiz: state.tracker.tookLmQuiz,
    tookMmQuiz: state.tracker.tookMmQuiz,
    appStage: state.app.appStage,
    lang: state.app.lang,
    kidsData: state.app.kidsData,
    headerState: state.header.headerState,
    quizState: state.quiz.quizState,
    learningModulesRedux: state?.app?.learningModulesRedux || [],
    recycleRight: state.recycleRight
  };
}

/**
 * Connect the HOC to Redux
 */
const AppContainer = compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps),
  HOC
);

export default AppContainer;
